import { cn } from '@lensora/ui'
import * as React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// Wraps an exact-match brand mention in any string leaf of `children` with a
// highlight <mark> — applied inside each block-level renderer below so brand
// highlighting survives proper Markdown rendering instead of requiring a
// separate hand-rolled plain-text path (which previously only stripped
// **bold** and left every other markdown construct — headings, lists —
// showing up as raw source text whenever a response happened to mention the
// brand).
function highlightBrandInChildren(children: React.ReactNode, brand?: string): React.ReactNode {
  if (!brand) return children
  const regex = new RegExp(`(${brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  return React.Children.map(children, (child) => {
    if (typeof child !== 'string') return child
    const parts = child.split(regex)
    if (parts.length === 1) return child
    return parts.map((part, i) =>
      part.toLowerCase() === brand.toLowerCase() ? (
        <mark key={i} className="rounded border border-feedback-success/30 bg-feedback-success/10 px-1 font-semibold text-feedback-success not-italic">
          {part}
        </mark>
      ) : (
        part
      )
    )
  })
}

function getMarkdownComponents(brand?: string) {
  const hl = (children?: React.ReactNode) => highlightBrandInChildren(children, brand)
  return {
    p: ({ children }: { children?: React.ReactNode }) => <p className="mb-2 text-sm leading-relaxed text-text last:mb-0">{hl(children)}</p>,
    strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-semibold text-text">{hl(children)}</strong>,
    h1: ({ children }: { children?: React.ReactNode }) => <h1 className="mb-1 mt-2 text-sm font-bold text-text">{hl(children)}</h1>,
    h2: ({ children }: { children?: React.ReactNode }) => <h2 className="mb-1 mt-2 text-sm font-bold text-text">{hl(children)}</h2>,
    h3: ({ children }: { children?: React.ReactNode }) => <h3 className="mb-1 mt-2 text-xs font-semibold text-text">{hl(children)}</h3>,
    ul: ({ children }: { children?: React.ReactNode }) => <ul className="mb-2 list-inside list-disc space-y-0.5 pl-1">{children}</ul>,
    li: ({ children }: { children?: React.ReactNode }) => <li className="text-sm text-text">{hl(children)}</li>,
    a: ({ children, href }: { children?: React.ReactNode; href?: string | undefined }) => (
      <a href={href ?? '#'} target="_blank" rel="noopener noreferrer" className="text-brand-accent underline hover:no-underline">{hl(children)}</a>
    ),
    table: ({ children }: { children?: React.ReactNode }) => (
      <div className="mb-2 overflow-x-auto"><table className="w-full border-collapse text-xs">{children}</table></div>
    ),
    th: ({ children }: { children?: React.ReactNode }) => (
      <th className="border border-border bg-surface-raised px-2 py-1.5 text-left text-xs font-semibold text-text">{hl(children)}</th>
    ),
    td: ({ children }: { children?: React.ReactNode }) => (
      <td className="border border-border px-2 py-1.5 text-xs text-text-muted">{hl(children)}</td>
    ),
  }
}

interface PlatformResponseBodyProps {
  text: string
  brandName?: string | undefined
  brandMentioned: boolean
}

// Extracted from the Prompts page's PlatformCard so onboarding's live demo
// preview and the Prompts page render LLM response text through one
// reviewed path (markdown + clip/expand + citation-strip) instead of two
// copies drifting apart. See ChatMessage.tsx for why this is the one
// sanctioned ReactMarkdown path in the app — no rehype-raw, no
// dangerouslySetInnerHTML.
export function PlatformResponseBody({ text, brandName, brandMentioned }: PlatformResponseBodyProps) {
  const [expanded, setExpanded] = React.useState(false)
  const hasResponse = !!text
  const isLong = text?.length > 400

  if (!hasResponse) return null

  const cleaned = text.replace(/\[\d+\]/g, '')

  return (
    <div className="relative">
      <div className={cn('overflow-hidden text-sm text-text', !expanded && isLong && 'max-h-[4.8em]')}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={getMarkdownComponents(brandMentioned ? brandName : undefined)}>
          {cleaned}
        </ReactMarkdown>
      </div>
      {!expanded && isLong && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-b from-transparent to-surface-raised" />
      )}
      {isLong && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="mt-2 text-xs text-brand-accent hover:underline"
        >
          {expanded ? 'Show less ↑' : 'Show more ↓'}
        </button>
      )}
    </div>
  )
}
